import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPortalSession,
  portalLoginSuccess,
  portalLogout,
} from "../../../store/slice/portalAuthSlice";
import { selectPortalId } from "../../../store/slice/portalContextSlice";
import { useLazyPortalAutoLoginQuery } from "../../../store/apiSlice/portalAuthApiSlice";
import { isAccessTokenExpired, getPortalSlugFromToken } from "../../../store/slice/portalAuthUtils";
import { PortalSpinner } from "./PortalBootstrap";
import { ButtonT1 } from "../../../components/atoms/Button";

/**
 * PortalPrivateRoutes
 *
 * Guards all private portal routes (catalog, orders, cart, etc.).
 * Responsibilities:
 *   1. Check if the user has a valid, non-expired access token in Redux, AND rich data (user).
 *   2. If token is present and valid → render the protected route immediately.
 *   3. If token is absent or expired, OR rich data is missing (e.g. hard refresh) → attempt a silent refresh via the
 *      HttpOnly subdomain-scoped cookie (GET /api/portal/v1/auth/autologin/?subdomain=...).
 *   4. If refresh succeeds → dispatch portalLoginSuccess and render.
 *   5. If refresh fails (no cookie, or expired) → redirect to /auth/login.
 *
 * Auth pages (login, forgot-password, reset_password, accept_invite) are
 * NEVER wrapped by this guard — they use PortalAuthRedirect instead.
 */
const PortalPrivateRoutes = () => {
  const { subdomain } = useParams();
  const dispatch = useDispatch();

  const portalSession = useSelector(state => selectPortalSession(state, subdomain));
  const accessToken = portalSession?.accessToken;
  const hasRichData = !!portalSession?.user;

  const portalId = useSelector(state => selectPortalId(state, subdomain));

  const [triggerAutoLogin] = useLazyPortalAutoLoginQuery();

  // Tri-state: null = not yet checked, true = refresh succeeded, false = refresh failed
  const [refreshStatus, setRefreshStatus] = useState(null);

  const hydrated = useSelector(state => state.portalAuth.hydrated);

  useEffect(() => {
    if (!hydrated) return;

    let active = true;

    // Token present, not expired, AND rich data exists → we can safely render
    if (accessToken && !isAccessTokenExpired(accessToken) && hasRichData) {
      const tokenSlug = getPortalSlugFromToken(accessToken);
      if (tokenSlug === subdomain) {
        setRefreshStatus(true);
        return;
      } else {
        // Token mismatch (e.g. cross-tenant tampering or tab mix)
        dispatch(portalLogout({ subdomain }));
        setRefreshStatus(false);
        return;
      }
    }

    // No valid token, OR missing rich data (new tab/refresh) → attempt autologin
    const attemptRefresh = async () => {
      try {
        const result = await triggerAutoLogin(subdomain).unwrap();
        if (!active) return;

        const tokenSlug = getPortalSlugFromToken(result.portal_access_token);

        if (tokenSlug === subdomain) {
          dispatch(
            portalLoginSuccess({
              subdomain,
              accessToken: result.portal_access_token,
              user: result.user,
              customerContact: result.customer_contact,
              salesRep: result.sales_rep,
              portalId,
              currency: result.currency,
              paymentTerm: result.payment_term,
              decimalPrecision: result.decimal_precision,
              portalConfig: result.portal_config,
            }),
          );
          setRefreshStatus(true);
        } else {
          // Resolved token slug mismatch
          dispatch(portalLogout({ subdomain }));
          setRefreshStatus(false);
        }
      } catch (err) {
        if (!active) return;
        if (err?.status === 401 || err?.status === 403 || err?.data?.code === "token_not_valid") {
          // Only clear the session if the backend explicitly rejected the token/cookie
          dispatch(portalLogout({ subdomain }));
          setRefreshStatus(false);
        } else {
          // 500 error or network timeout. Do not redirect to login.
          setRefreshStatus("error");
        }
      }
    };

    attemptRefresh();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain, hydrated, accessToken, portalId, dispatch, triggerAutoLogin]);

  // Still waiting for refresh result
  if (refreshStatus === null) {
    return <PortalSpinner size="large" />;
  }

  // Network or 500 error → show offline screen, preserve session
  if (refreshStatus === "error") {
    return (
      <div style={{ textAlign: "center", marginTop: "100px", fontFamily: "Inter, sans-serif" }}>
        <h2 style={{ color: "#333", fontSize: "20px", marginBottom: "8px" }}>Unable to connect</h2>
        <p style={{ color: "#666", fontSize: "14px", marginBottom: "24px" }}>
          Please check your internet connection or try again later.
        </p>
        <ButtonT1
          title="Retry"
          $variant="primary"
          onClickCallback={() => window.location.reload()}
        />
      </div>
    );
  }

  // Refresh failed → send to login
  if (refreshStatus === false) {
    return <Navigate to={`/portal/${subdomain}/auth/login`} replace />;
  }

  // Authenticated → render the protected page
  return <Outlet />;
};

export default PortalPrivateRoutes;
